import { useCallback, useEffect, useRef } from 'react'

import { AppId } from '@inno-tools/container-bridge'
import { useAvailableApps } from 'modules/app-registry/hooks/useAvailableApps'
import { useLocation, useNavigate } from 'react-router-dom'
import { CustomEvents } from 'shared/constants/customEvents'
import { useHistory, useUserContext } from 'shared/hooks'
import { useCommonStore } from 'shared/stores/commonStore'
import { dispatchCustomEvent } from 'shared/utils/customEvents'

import { FullPageAppTemplate } from '../../templates'

type ClusterMeetState = 'online' | 'offline'

const ClusterMeetApp = () => {
  const clusterMeetStateRef = useRef<ClusterMeetState>('offline')
  const navigate = useNavigate()
  const location = useLocation()
  const history = useHistory()
  const { user } = useUserContext()
  const { apps } = useAvailableApps()
  const setIsClusterMeetOnline = useCommonStore(
    state => state.setIsClusterMeetOnline
  )

  const reportClusterMeetState = useCallback(
    (isOnline: boolean) => {
      setIsClusterMeetOnline(isOnline)
      // The parent needs this only when Cluster Meet runs in an iframe;
      // same-context pages already share the Zustand store directly.
      if (window.parent !== window) {
        dispatchCustomEvent(
          CustomEvents.CLUSTER_MEET_STATE_CHANGED,
          { isOnline },
          window.parent
        )
      }
    },
    [setIsClusterMeetOnline]
  )

  // Function to update Cluster Meet state from the Cluster Meet app
  const setClusterMeetState = useCallback(
    (state: ClusterMeetState) => {
      clusterMeetStateRef.current = state
      reportClusterMeetState(state === 'online')
    },
    [reportClusterMeetState]
  )

  useEffect(
    () => () => {
      reportClusterMeetState(false)
    },
    [reportClusterMeetState]
  )

  return (
    <FullPageAppTemplate
      appId={AppId.CLUSTER_MEET}
      scope='cluster_meet'
      envKey={apps[AppId.CLUSTER_MEET].runtimeEnvKey}
      entryPath='./ClusterMeetApp'
      url={process.env.REACT_APP_CLUSTER_MEET_APP_DEV_PATH}
      cdnUrl={apps[AppId.CLUSTER_MEET].cdnUrl}
      appName={apps[AppId.CLUSTER_MEET].name}
      appProps={{
        user,
        history,
        appPath: apps[AppId.CLUSTER_MEET].path,
        initialPath: location.pathname,
        clusterMeetState: clusterMeetStateRef.current,
        setClusterMeetState,
        onNavigate: ({ pathname: nextPathname }: { pathname: string }) => {
          const { pathname } = location
          if (pathname !== nextPathname) {
            navigate(nextPathname)
          }
        }
      }}
    />
  )
}

export { ClusterMeetApp }
